import { type LogicCondition, LogicSubConditonType } from '@webapp/common/entities/logic';
import { LogicType } from '@webapp/common/resources/survey';

import type { Page } from '../common';
import type { States } from '../states';

import { variantsWithValues } from './lib';
import type { LogicState } from './logic';
import type { LogicPageQuestion } from './question';

export const selectLogic = ({ Logic }: States): LogicState => Logic;

export const selectLogicPages = (states: States): Array<Page> => {
    const l = selectLogic(states);

    return l.pages;
};
export const selectIsExtendedLogicLoading = (states: States): boolean => {
    const l = selectLogic(states);

    return l.fetchExtendedLogics;
};

export const selectLastAddedExtendedConditionId = (states: States): number | null => {
    const l = selectLogic(states);

    return l.lastAddedExtendedConditionId;
};
export const selectExtendedLogic = (states: States): Array<LogicCondition> => {
    const l = selectLogic(states);

    return l.extednedLogics;
};

export const selectExtendedLogicAvaliableQuestions = (states: States): Array<LogicPageQuestion> =>
    selectAllQuestions(states).filter(
        // обязательные вопросы без вариантов доступны, только если у них есть «Не могу оценить» —
        // иначе для них нет ни одного условия
        (q) => !q.params.required || variantsWithValues.has(q.type) || q.params.cantRate,
    );

export const selectLogicsLoading = (states: States): boolean => selectLogic(states).fetchLogics;

// Квоты на весь опрос (тип 4): лежат в logics, а не в pages
export const selectSurveyQuotas = (states: States): Array<LogicCondition> => selectLogic(states).logics;

export const selectQuotas = (states: States): Array<LogicCondition & { question?: LogicPageQuestion; page?: Page }> => {
    const l = selectLogic(states);

    // Порядок — по id (т.е. по порядку создания), а не по расположению вопроса:
    // иначе новая квота встаёт вверх (addQuota привязывает её к первому вопросу),
    // а при смене вопроса квота «прыгает» по списку.
    return l.logics
        .concat(
            l.pages.flatMap((page) =>
                page.questions.flatMap((question) =>
                    question.conditions
                        .filter((condition) => condition.respondent === LogicType.TYPE_QUESTION_QUOTA)
                        .map((cond) => ({ ...cond, question, page })),
                ),
            ),
        )
        .sort((a, b) => a.id - b.id);
};

export const selectAllQuestionsCount = (states: States): number => {
    const ps = selectLogicPages(states);

    return ps.reduce((acc, p) => acc + p.questions.length, 0);
};

export const selectAllQuestions = (states: States): Array<LogicPageQuestion> => {
    const ps = selectLogicPages(states);

    return ps.flatMap((p) => p.questions);
};

export const selectQuestionById =
    (questionId: number) =>
    (states: States): LogicPageQuestion => {
        const qs = selectAllQuestions(states);

        return qs.find(({ id }) => id === questionId);
    };

export const selectAllQuestionsIds = (states: States): Array<number> => {
    const ps = selectLogicPages(states);

    return ps.flatMap((p) => p.questions.map((q) => q.id));
};

export const isAnyQuestionConditionInLogicSelector =
    (logicId: number) =>
    (states: States): boolean => {
        const extendedLogic = selectExtendedLogic(states);
        const logic = extendedLogic.find((l) => l.id === logicId);

        return logic.conditions.some((c) => c.type === LogicSubConditonType.QUESTION);
    };

export const selectFirstAvailiableQuestionIdByLogicId =
    (logicId: number) =>
    (states: States): number => {
        const ps = selectLogicPages(states);
        const extendedLogic = selectExtendedLogic(states);
        const logic = extendedLogic.find((l) => l.id === logicId);
        const isAnyQuestionConditionInLogic = logic?.conditions?.some((c) => c.type === LogicSubConditonType.QUESTION);
        if (!isAnyQuestionConditionInLogic) return ps?.[0]?.questions?.[0]?.id;
        const questionIds = logic.conditions
            .filter((c) => c.type === LogicSubConditonType.QUESTION)
            .map((c) => c.questionId)
            .filter(Boolean);
        const firstQuestionId = questionIds
            .map((qId) => {
                let order = 0;
                ps.forEach((p) => {
                    const q = p.questions.find((q) => q.id === qId);
                    if (q) {
                        order = q.order;
                    }
                });
                return { qId, order };
            })
            .sort((a, b) => b.order - a.order)?.[0]?.qId;

        return firstQuestionId ?? ps?.[0]?.questions?.[0]?.id;
    };
